"use client"
import { X, Save, Loader, Plus, Trash2, Type, AlignLeft, FileText } from "lucide-react"

export const NotesEditorModal = ({
  isOpen,
  onClose,
  notesData,
  jsonData,
  isLoading,
  isSaving,
  onUpdateContent,
  onUpdateTitle,
  onSave,
}) => {
  if (!isOpen) return null

  const addNewSection = () => {
    if (!jsonData || !jsonData.notes) return

    // Add a new main section
    const newSection = {
      heading: "",
      content: "",
      sub_entries: [],
    }

    const updatedNotes = [...jsonData.notes, newSection]
    // Call the parent's update function with the correct parameters
    onUpdateContent(jsonData.notes.length, "heading", "", null, updatedNotes)
  }

  const addSubSection = (noteIndex) => {
    if (!jsonData || !jsonData.notes || !jsonData.notes[noteIndex]) return

    // Add a new sub-section to an existing section
    const newSubEntry = {
      heading: "",
      content: "",
    }

    const currentNote = jsonData.notes[noteIndex]
    const updatedSubEntries = [...(currentNote.sub_entries || []), newSubEntry]
    onUpdateContent(noteIndex, "sub_entries", updatedSubEntries)
  }

  const removeSection = (noteIndex) => {
    if (!jsonData || !jsonData.notes) return

    const updatedNotes = jsonData.notes.filter((_, index) => index !== noteIndex)
    // Update the entire notes array
    onUpdateContent(0, "notes", updatedNotes)
  }

  const removeSubSection = (noteIndex, subIndex) => {
    if (!jsonData || !jsonData.notes || !jsonData.notes[noteIndex]) return

    const currentNote = jsonData.notes[noteIndex]
    const updatedSubEntries = currentNote.sub_entries.filter((_, index) => index !== subIndex)
    onUpdateContent(noteIndex, "sub_entries", updatedSubEntries)
  }

  const renderTextBlock = (content, noteIndex, field, subEntryIndex = null, placeholder = "", isHeading = false) => {
    const baseClasses =
      "w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 resize-vertical"
    const headingClasses = isHeading ? "font-semibold text-lg" : ""

    return (
      <div className="mb-6">
        <textarea
          value={content || ""}
          onChange={(e) => onUpdateContent(noteIndex, field, e.target.value, subEntryIndex)}
          placeholder={placeholder}
          className={`${baseClasses} ${headingClasses} ${isHeading ? "min-h-[60px] bg-gradient-to-r from-blue-50 to-indigo-50" : "min-h-[120px] bg-white"}`}
          rows={isHeading ? 2 : 5}
        />
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 z-50">
      {/* Full Screen Container */}
      <div className="h-full flex flex-col bg-white">
        {/* Compact Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg">
          <div className="px-6 py-3">
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                  <FileText className="h-5 w-5" />
                </div>
                <div>
                  <h1 className="text-xl font-bold">Notes Editor</h1>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <button
                  onClick={addNewSection}
                  className="flex items-center px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-all duration-200 backdrop-blur-sm"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Section
                </button>

                <button
                  onClick={onSave}
                  disabled={isSaving || isLoading}
                  className="flex items-center px-4 py-2 bg-green-500 hover:bg-green-600 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                >
                  {isSaving ? (
                    <>
                      <Loader className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save
                    </>
                  )}
                </button>

                <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-all duration-200">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Side - Editor */}
          <div className="w-1/2 border-r border-gray-200 overflow-y-auto bg-gray-50">
            <div className="p-6">
              {isLoading ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <div className="relative">
                    <div className="w-12 h-12 border-4 border-blue-200 rounded-full animate-spin"></div>
                    <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin absolute top-0"></div>
                  </div>
                  <p className="mt-4 text-lg text-gray-600 font-medium">Loading your notes...</p>
                </div>
              ) : jsonData ? (
                <div className="max-w-4xl mx-auto space-y-6">
                  {/* Document Title */}
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center mb-3">
                      <Type className="h-4 w-4 text-blue-600 mr-2" />
                      <label className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                        Document Title
                      </label>
                    </div>
                    <input
                      type="text"
                      value={jsonData.title || ""}
                      onChange={(e) => onUpdateTitle(e.target.value)}
                      className="w-full p-3 text-xl font-bold border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gradient-to-r from-blue-50 to-indigo-50"
                      placeholder="Enter your document title..."
                    />
                  </div>

                  {/* Notes Sections */}
                  <div className="space-y-4">
                    {jsonData.notes &&
                      jsonData.notes.map((note, noteIndex) => (
                        <div
                          key={noteIndex}
                          className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
                        >
                          {/* Section Header */}
                          <div className="bg-gradient-to-r from-gray-50 to-blue-50 px-4 py-3 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center">
                                <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-2">
                                  {noteIndex + 1}
                                </div>
                                <h3 className="text-md font-semibold text-gray-800">Section {noteIndex + 1}</h3>
                              </div>
                              <button
                                onClick={() => removeSection(noteIndex)}
                                className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors duration-200"
                                title="Remove Section"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>

                          <div className="p-4">
                            {/* Main Heading */}
                            <div className="mb-4">
                              <div className="flex items-center mb-2">
                                <Type className="h-4 w-4 text-blue-600 mr-2" />
                                <label className="text-sm font-medium text-gray-700">Heading</label>
                              </div>
                              {renderTextBlock(
                                note.heading,
                                noteIndex,
                                "heading",
                                null,
                                "Enter section heading...",
                                true,
                              )}
                            </div>

                            {/* Main Content */}
                            <div className="mb-4">
                              <div className="flex items-center mb-2">
                                <AlignLeft className="h-4 w-4 text-green-600 mr-2" />
                                <label className="text-sm font-medium text-gray-700">Content</label>
                              </div>
                              {renderTextBlock(note.content, noteIndex, "content", null, "Enter section content...")}
                            </div>

                            {/* Sub Sections */}
                            {note.sub_entries && note.sub_entries.length > 0 && (
                              <div className="border-l-4 border-blue-200 pl-4 ml-2">
                                <div className="flex items-center justify-between mb-3">
                                  <h4 className="text-sm font-semibold text-gray-700 flex items-center">
                                    <div className="w-2 h-2 bg-blue-600 rounded-full mr-2"></div>
                                    Sub-sections
                                  </h4>
                                </div>

                                <div className="space-y-3">
                                  {note.sub_entries.map((subEntry, subIndex) => (
                                    <div
                                      key={subIndex}
                                      className="bg-gradient-to-r from-gray-50 to-blue-50 p-3 rounded-lg border border-gray-200"
                                    >
                                      <div className="flex items-center justify-between mb-2">
                                        <span className="text-xs font-medium text-gray-600">
                                          Sub-section {subIndex + 1}
                                        </span>
                                        <button
                                          onClick={() => removeSubSection(noteIndex, subIndex)}
                                          className="p-1 text-red-500 hover:bg-red-100 rounded transition-colors duration-200"
                                          title="Remove Sub-section"
                                        >
                                          <Trash2 className="h-3 w-3" />
                                        </button>
                                      </div>

                                      <div className="mb-2">
                                        <label className="block text-xs font-medium text-gray-600 mb-1">
                                          Sub-heading
                                        </label>
                                        {renderTextBlock(
                                          subEntry.heading,
                                          noteIndex,
                                          "heading",
                                          subIndex,
                                          "Enter sub-heading...",
                                          true,
                                        )}
                                      </div>

                                      <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-1">
                                          Sub-content
                                        </label>
                                        {renderTextBlock(
                                          subEntry.content,
                                          noteIndex,
                                          "content",
                                          subIndex,
                                          "Enter sub-content...",
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Add Sub-section Button */}
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <button
                                onClick={() => addSubSection(noteIndex)}
                                className="flex items-center px-3 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200 border border-blue-200 hover:border-blue-300 text-sm"
                              >
                                <Plus className="h-4 w-4 mr-1" />
                                Add Sub-section
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-20">
                  <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mb-4">
                    <FileText className="h-8 w-8 text-gray-400" />
                  </div>
                  <p className="text-xl text-gray-500 font-medium">No notes data available</p>
                  <p className="text-gray-400 mt-2">Start by adding some content to your notes</p>
                </div>
              )}
            </div>
          </div>

          {/* Right Side - PDF Preview */}
          <div className="w-1/2 bg-white flex flex-col">
            <div className="bg-gradient-to-r from-gray-50 to-blue-50 px-4 py-3 border-b border-gray-200">
              <div className="flex items-center">
                <FileText className="h-4 w-4 text-blue-600 mr-2" />
                <h3 className="text-md font-semibold text-gray-800">Original PDF</h3>
              </div>
            </div>

            <div className="flex-1 relative">
              {notesData?.url ? (
                <iframe src={notesData.url} className="w-full h-full border-0" title="PDF Preview">
                  <div className="flex items-center justify-center h-full bg-gray-50">
                    <p className="text-gray-500">Your browser does not support embedded PDFs.</p>
                  </div>
                </iframe>
              ) : (
                <div className="flex flex-col items-center justify-center h-full bg-gray-50">
                  <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mb-4">
                    <FileText className="h-8 w-8 text-gray-400" />
                  </div>
                  <p className="text-lg text-gray-500 font-medium">PDF Unavailable</p>
                  <p className="text-gray-400 mt-2">The original PDF could not be loaded</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
